import type { VisibilityLevel } from './constants/visibilityKeys';
import { VISIBILITY_LEVEL } from './constants/visibilityKeys';

export type VisibilityConfigMap = Record<string, number | boolean | undefined> | null | undefined;

/** Default when config missing or key missing: Read (0). */
const DEFAULT_LEVEL: VisibilityLevel = VISIBILITY_LEVEL.READ;

/**
 * Normalize API/legacy value to VisibilityLevel.
 * true -> 1, false -> -1, -1|0|1 -> as-is.
 */
export function normalizeLevel(value: unknown): VisibilityLevel | undefined {
    if (typeof value === 'number' && (value === -1 || value === 0 || value === 1)) {
        return value as VisibilityLevel;
    }
    return undefined;
}

/**
 * Get normalized level for a key. Always returns a level: default 0 (Read) when config missing or key missing.
 * Handles all three scenarios: -1 (Hidden), 0 (Read), 1 (Write).
 */
export function getLevel(config: VisibilityConfigMap, key: string): VisibilityLevel {
    if (!config || typeof config !== 'object') return DEFAULT_LEVEL;
    const raw = config[key];
    const level = normalizeLevel(raw);
    return level ?? DEFAULT_LEVEL;
}

/**
 * Global helper: one call gives both "show" and "disabled" for a key.
 * Use for any feature that should be hidden when -1 and disabled when 0.
 */
export type FeatureVisibility = { show: boolean; disabled: boolean; hidden: boolean; write?: boolean };

export function getFeatureVisibility(config: VisibilityConfigMap, key: string): FeatureVisibility {
    const level = getLevel(config, key);
    return {
        show: level >= VISIBILITY_LEVEL.READ,
        disabled: level === VISIBILITY_LEVEL.READ,
        hidden: level === VISIBILITY_LEVEL.HIDDEN,
        write: level === VISIBILITY_LEVEL.WRITE,
    };
}
